import Fuse from 'fuse.js';
import { action, autorun, computed, makeObservable, observable } from 'mobx';
import { IImageProps, ISuggestionItem } from 'telia-front-react';
import isNumeric from 'validator/es/lib/isNumeric';
import ErrorUtil from '../error/ErrorUtil';
import { CountryWithCallingCode } from '../roaming/RoamingInterfaces';
import { allCountries, FLAG_IMAGE_BASEURL, getCountryLabel, mapCountryToNumberSuggestion } from '../util/CountryUtil';
import LanguageStore, { Language } from './LanguageStore';
import RootStore from './RootStore';

export const removePlusAndEstonianAreaCode = (phone: string) => {
  return phone.replace(/^(\+372|372|\+)/, '');
};

interface CallingCodeSuggestion extends ISuggestionItem {
  synonym: string;
  callingCode: string;
}

class CountryCallingCodeStore {
  @observable accessor callingCodeSuggestionSearchTerm: string = '';
  @observable accessor allCallingCodeSuggestions: CallingCodeSuggestion[];
  @observable accessor currentCallingCodeSuggestions: CallingCodeSuggestion[] = [];
  @observable accessor selectedCountry: CountryWithCallingCode;
  @observable accessor selectedCountryImage: IImageProps = null;
  @observable accessor countryList: {
    countries: CountryWithCallingCode[];
    status: string;
  } = {
    countries: [],
    status: 'UNINITIALIZED'
  };
  @observable accessor fuzzySearch: Fuse<CallingCodeSuggestion>;

  private readonly fuzzySearchOptions = {
    shouldSort: true,
    keys: ['label', 'synonym'],
    threshold: 0.3
  };
  private languageStore: LanguageStore;
  private disposeLiveUpdate: () => void;

  @action.bound
  public initializeDefaultCountry() {
    if (this.selectedCountry) {
      return;
    }
    if (this.countryList.status === 'FETCHED') {
      const estoniaCallingCodeSuggestion = this.allCallingCodeSuggestions.find((suggestion: CallingCodeSuggestion) => suggestion.id === 'EST');
      const estonia = this.countryList.countries.find((country: CountryWithCallingCode) => country.isoCode === 'EST');

      this.currentCallingCodeSuggestions = this.allCallingCodeSuggestions;
      this.callingCodeSuggestionSearchTerm = estoniaCallingCodeSuggestion.label;
      this.selectedCountry = estonia;
      this.selectedCountryImage = { src: `${FLAG_IMAGE_BASEURL}EST`, role: 'presentation' };
    }
  }

  constructor(rootStore: RootStore) {
    makeObservable(this);
    this.languageStore = rootStore.languageStore;
  }

  public subscribe() {
    if (!this.disposeLiveUpdate) {
      this.disposeLiveUpdate = autorun(() => {
        this.populateCountryList();
      });
    }
  }

  public unsubscribe() {
    if (this.disposeLiveUpdate) {
      this.disposeLiveUpdate();
      delete this.disposeLiveUpdate;
    }
  }

  @action
  public handleInputChange(input: string) {
    input = this.prettifyNumericInput(input);
    this.callingCodeSuggestionSearchTerm = input;

    if (!!this.selectedCountry && this.selectedCountry.searchTerm !== this.callingCodeSuggestionSearchTerm) {
      this.selectedCountry = null;
      this.selectedCountryImage = null;
    }
    if (!this.shouldShowSuggestions(input)) {
      return;
    }
    this.currentCallingCodeSuggestions = isNumeric(input)
      ? this.getCountrySuggestionsByCallingCode(input)
      : this.fuzzySearch.search(input)?.map((result) => result.item);
  }

  @action
  public selectCallingCodeSuggestionItem(callingCodeSuggestionItem: ISuggestionItem) {
    this.selectedCountry = this.getCountryByIsoCode(callingCodeSuggestionItem.id);
    this.selectedCountryImage = {
      src: `${FLAG_IMAGE_BASEURL}${this.selectedCountry.isoCode}`,
      role: 'presentation'
    };
    this.selectedCountry.searchTerm = getCountryLabel(this.selectedCountry);
  }

  @computed
  public get countryCallingCode() {
    return this.selectedCountry ? `+${this.selectedCountry.callingCode}` : '';
  }

  @computed
  public get isForeignCodeSelected(): boolean {
    return !!this.selectedCountry && this.selectedCountry.isoCode !== 'EST';
  }

  @computed
  get callingCodeSuggestionItems() {
    return this.callingCodeSuggestionSearchTerm && this.shouldShowSuggestions(this.callingCodeSuggestionSearchTerm)
      ? this.currentCallingCodeSuggestions.slice()
      : [];
  }

  @action
  private async populateCountryList() {
    try {
      this.countryList.countries = allCountries;
      this.allCallingCodeSuggestions = this.countryList.countries.map((country) => this.mapCountryToCallingCodeSuggestion(country));
      this.countryList.status = 'FETCHED';

      this.fuzzySearch = new Fuse(this.allCallingCodeSuggestions, this.fuzzySearchOptions);
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
    }
  }

  private getCountryByIsoCode(isoCode: string) {
    return this.countryList.countries.find((country) => country.isoCode === isoCode);
  }

  private getCountrySuggestionsByCallingCode(input: string): CallingCodeSuggestion[] {
    return this.allCallingCodeSuggestions.filter((country) => `+${country.callingCode}`.startsWith(input));
  }

  private prettifyNumericInput(input: string) {
    if (input && isNumeric(input) && !input.startsWith('+')) {
      return `+${input}`;
    }
    return input;
  }

  private mapCountryToCallingCodeSuggestion(country: CountryWithCallingCode, lang: Language = this.languageStore.currentLanguage): CallingCodeSuggestion {
    return mapCountryToNumberSuggestion(country, lang);
  }

  private shouldShowSuggestions(input: string) {
    return !!input && input !== '+' && (!isNumeric(input) || input === '+1' || input === '+7' || input.length > 2);
  }
}

export default CountryCallingCodeStore;
